/**
 * Stock-Out Management Dashboard - Enhanced with Root Cause Analysis
 * Comprehensive view of stock-outs with root cause analysis, trends, and prevention
 */
import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  Autocomplete,
  LinearProgress,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Lightbulb as RecommendationIcon,
  Schedule as TimeIcon,
  ExpandMore as ExpandMoreIcon,
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import UniversalFilters from '../common/UniversalFilters';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import ExportMenu from '../Common/ExportMenu';
import { useFullscreen, FullscreenButton } from '../Common/FullscreenButton';
import {
  StockOutDashboardSummary,
  StockOutPeriod,
  RootCauseAnalysis,
  StockOutTrends,
  PreventionRecommendation,
} from '../../types/stockout';
import {
  StockoutsWithRootCauseResponse,
  FilterOptions,
  StockoutAnalyticsByRootCause,
  RootCauseType,
} from '../../types/executive';

const COLORS = ['#722F37', '#B76E79', '#C9A96E', '#8B7355', '#8B6F8B', '#DEB5A0'];
const ROOT_CAUSE_COLORS: Record<string, string> = {
  'brand_supply_issue': '#722F37',
  'forecast_accuracy_issue': '#C9A96E',
  'distributor_issue': '#8B7355',
  'planning_issue': '#B76E79',
  'unknown': '#9CA3AF',
};
const chartTooltipStyle = {
  background: '#FDFBF7',
  border: '1px solid #E8E0D4',
  borderRadius: '8px',
  boxShadow: '0 4px 20px rgba(114,47,55,0.08)',
  fontSize: '12px',
  padding: '10px 14px',
};

interface AnnouncedStockoutItem {
  product_id: string;
  product_name: string;
  sku: string;
  start_date: string;
  end_date: string | null;
}

/** Map root cause to a concise, actionable recommendation */
const getRecommendationForRootCause = (
  rootCause: RootCauseType | string,
  announcedMatch: AnnouncedStockoutItem | undefined,
): string => {
  switch (rootCause) {
    case 'brand_supply_issue':
      return announcedMatch
        ? `Supplier announced OOS (${new Date(announcedMatch.start_date).toLocaleDateString()}${announcedMatch.end_date ? ` – ${new Date(announcedMatch.end_date).toLocaleDateString()}` : ' – TBD'}). Wait for supplier resolution or source alternative.`
        : 'Escalate to brand/supplier — request expedited delivery confirmation and review alternative sources.';
    case 'distributor_issue':
      return announcedMatch
        ? `Linked to supplier-announced OOS (${new Date(announcedMatch.start_date).toLocaleDateString()}${announcedMatch.end_date ? ` – ${new Date(announcedMatch.end_date).toLocaleDateString()}` : ' – TBD'}). Contact distributor for reallocation or inter-store transfer.`
        : 'Request stock transfer from nearby stores with surplus. Contact distributor to prioritize allocation.';
    case 'forecast_accuracy_issue':
      return 'Review and adjust forecast parameters. Increase safety stock buffer and place immediate replenishment order based on actual demand.';
    case 'planning_issue':
      return 'Place urgent replenishment order. Review reorder point settings — currently too low. Enable automated ordering for this SKU.';
    default:
      return 'Investigate root cause. Place immediate replenishment order and review all supply chain touchpoints.';
  }
};

const StockOutManagement: React.FC = () => {
  const { fmtCurrency, fmtCurrencyLabel } = useCurrencyFormat();
  const navigate = useNavigate();
  const { isFullscreen, toggle: toggleFullscreen, FullscreenWrap } = useFullscreen();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [currentTab, setCurrentTab] = useState(0);

  // Universal filters state
  const [filters, setFilters] = useState<{
    brands: string[];
    stores: string[];
    categories: string[];
    cities: string[];
    abcClassifications: string[];
    root_cause?: string;
    is_resolved?: boolean;
    start_date: string;
    end_date: string;
  }>({
    brands: [],
    stores: [],
    categories: [],
    cities: [],
    abcClassifications: [],
    start_date: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });

  // Dialog state for action plan and order history
  const [actionPlanDialogOpen, setActionPlanDialogOpen] = useState(false);
  const [orderHistoryDialogOpen, setOrderHistoryDialogOpen] = useState(false);
  const [selectedStockout, setSelectedStockout] = useState<any>(null);

  // Fetch filter options
  useQuery<FilterOptions>({
    queryKey: ['executiveFilterOptions'],
    queryFn: async () => {
      const response = await api.getExecutiveFilterOptions();
      return response.data;
    },
  });

  // Fetch enhanced stockouts with root cause
  const { data: enhancedStockouts, isLoading: loadingEnhancedStockouts } = useQuery<StockoutsWithRootCauseResponse>({
    queryKey: ['enhancedStockouts', filters],
    queryFn: async () => {
      const response = await api.getStockoutsWithRootCause({
        brands: filters.brands.length > 0 ? filters.brands.join(',') : undefined,
        stores: filters.stores.length > 0 ? filters.stores.join(',') : undefined,
        categories: filters.categories.length > 0 ? filters.categories.join(',') : undefined,
        cities: filters.cities.length > 0 ? filters.cities.join(',') : undefined,
        abc_classifications: filters.abcClassifications.length > 0 ? filters.abcClassifications.join(',') : undefined,
        root_cause: filters.root_cause,
        is_resolved: filters.is_resolved,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        limit: 100,
        offset: 0,
      });
      return response.data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Fetch stockout analytics by root cause
  const { data: rootCauseAnalytics } = useQuery<StockoutAnalyticsByRootCause>({
    queryKey: ['stockoutRootCauseAnalytics', filters],
    queryFn: async () => {
      const response = await api.getStockoutAnalyticsByRootCause({
        brands: filters.brands.length > 0 ? filters.brands.join(',') : undefined,
        stores: filters.stores.length > 0 ? filters.stores.join(',') : undefined,
        categories: filters.categories.length > 0 ? filters.categories.join(',') : undefined,
        cities: filters.cities.length > 0 ? filters.cities.join(',') : undefined,
        abc_classifications: filters.abcClassifications.length > 0 ? filters.abcClassifications.join(',') : undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
      });
      return response.data;
    },
  });

  // Fetch announced stockouts from replenishment settings
  const { data: announcedStockoutsData } = useQuery<{ items?: AnnouncedStockoutItem[] }>({
    queryKey: ['announcedStockouts'],
    queryFn: async () => {
      const response = await api.getSettings('announced_stockouts');
      return response.data || {};
    },
  });
  const announcedStockouts = announcedStockoutsData?.items || [];

  // Fetch monthly stockout value for chart (grouped by root cause)
  const { data: monthlyStockoutRaw } = useQuery<{ months: Record<string, any>[]; root_causes: string[] }>({
    queryKey: ['monthlyStockoutValue', filters],
    queryFn: async () => {
      const response = await api.getMonthlyStockoutValue({
        brands: filters.brands.length > 0 ? filters.brands.join(',') : undefined,
        stores: filters.stores.length > 0 ? filters.stores.join(',') : undefined,
        categories: filters.categories.length > 0 ? filters.categories.join(',') : undefined,
        cities: filters.cities.length > 0 ? filters.cities.join(',') : undefined,
        abc_classifications: filters.abcClassifications.length > 0 ? filters.abcClassifications.join(',') : undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
      });
      return response.data;
    },
  });

  // Root cause keys for stacked bars
  const rootCauseKeys = useMemo(() => monthlyStockoutRaw?.root_causes || [], [monthlyStockoutRaw]);

  // Build Jan–Dec of the current year, filling in empty months with zeros
  const monthlyChartData = useMemo(() => {
    const dataMap = new Map<string, Record<string, any>>();
    (monthlyStockoutRaw?.months || []).forEach((item) => {
      dataMap.set(item.month, item);
    });

    const year = new Date().getFullYear();
    const months: Record<string, any>[] = [];
    for (let m = 0; m < 12; m++) {
      const key = `${year}-${String(m + 1).padStart(2, '0')}`;
      const label = new Date(year, m, 1).toLocaleDateString(undefined, { month: 'short' });
      const existing = dataMap.get(key);
      const row: Record<string, any> = {
        month: key,
        label,
        lost_revenue: existing?.lost_revenue || 0,
        stockout_count: existing?.stockout_count || 0,
      };
      // Fill each root cause key
      for (const rc of rootCauseKeys) {
        row[rc] = existing?.[rc] || 0;
      }
      months.push(row);
    }
    return months;
  }, [monthlyStockoutRaw, rootCauseKeys]);

  // Fetch dashboard summary (legacy - for summary cards)
  const { data: summary, isLoading: loadingSummary } = useQuery<StockOutDashboardSummary>({
    queryKey: ['stockoutSummary'],
    queryFn: async () => {
      const response = await api.getStockOutDashboardSummary();
      return response.data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Keep legacy API calls for other tabs
  // Fetch all stock-outs (legacy)
  useQuery<StockOutPeriod[]>({
    queryKey: ['stockoutPeriods'],
    queryFn: async () => {
      const response = await api.detectStockOutPeriods();
      return response.data;
    },
    enabled: false, // Disabled - using enhanced API instead
  });

  // Fetch root cause analysis
  const { data: rootCause } = useQuery<RootCauseAnalysis>({
    queryKey: ['rootCauseAnalysis'],
    queryFn: async () => {
      const response = await api.getRootCauseAnalysis();
      return response.data;
    },
  });

  // Fetch trends
  const { data: trends } = useQuery<StockOutTrends>({
    queryKey: ['stockoutTrends'],
    queryFn: async () => {
      const response = await api.getStockOutTrends(90);
      return response.data;
    },
  });

  // Fetch recommendations
  const { data: recommendations } = useQuery<PreventionRecommendation[]>({
    queryKey: ['preventionRecommendations'],
    queryFn: async () => {
      const response = await api.getPreventionRecommendations();
      return response.data;
    },
  });

  // Fetch detailed order history for selected stockout
  const { data: orderHistory, isLoading: loadingOrderHistory } = useQuery({
    queryKey: ['orderHistory', selectedStockout?.store.store_id, selectedStockout?.product.product_id],
    queryFn: async () => {
      if (!selectedStockout) return null;
      const response = await api.getDetailedOrderHistory(
        selectedStockout.store.store_id,
        selectedStockout.product.product_id,
        90
      );
      return response.data;
    },
    enabled: !!selectedStockout && orderHistoryDialogOpen,
  });

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'status-badge status-badge--danger';
      case 'high':
        return 'status-badge status-badge--warning';
      case 'medium':
        return 'status-badge status-badge--neutral';
      default:
        return 'status-badge status-badge--success';
    }
  };

  const getTrendIcon = (direction: string) => {
    if (direction === 'increasing') return <TrendingUpIcon sx={{ color: 'var(--color-danger)' }} />;
    if (direction === 'decreasing') return <TrendingDownIcon sx={{ color: 'var(--color-success)' }} />;
    return <CheckIcon sx={{ color: 'var(--color-info)' }} />;
  };

  const formatRootCause = (rootCause: string): string => {
    return rootCause.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getRootCauseColor = (rootCause: string) => {
    return ROOT_CAUSE_COLORS[rootCause] || ROOT_CAUSE_COLORS['unknown'];
  };

  const getConfidenceColor = (confidence: number): 'error' | 'warning' | 'info' | 'success' => {
    if (confidence >= 85) return 'success';
    if (confidence >= 70) return 'info';
    if (confidence >= 50) return 'warning';
    return 'error';
  };

  // Handler functions for dialogs
  const handleOpenActionPlan = (stockout: any) => {
    setSelectedStockout(stockout);
    setActionPlanDialogOpen(true);
  };

  const handleOpenOrderHistory = (stockout: any) => {
    setSelectedStockout(stockout);
    setOrderHistoryDialogOpen(true);
  };

  const handleCloseDialogs = () => {
    setActionPlanDialogOpen(false);
    setOrderHistoryDialogOpen(false);
  };

  // Prepare data for charts (enhanced with root cause analytics)
  const rootCauseChartData = rootCauseAnalytics
    ? rootCauseAnalytics.analytics_by_root_cause.map((item) => ({
        name: formatRootCause(item.root_cause),
        value: item.stockout_count,
        revenue: item.total_lost_revenue,
      }))
    : rootCause
    ? Object.entries(rootCause.cause_breakdown).map(([cause, data]) => ({
        name: cause.replace(/_/g, ' ').toUpperCase(),
        value: data.count,
        revenue: data.total_lost_revenue,
      }))
    : [];

  return (
    <FullscreenWrap>
    <Box>
      <Box className="page-enter">
        {/* Universal Filters */}
        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: 2, mb: 3 }}>
          <UniversalFilters
            brands={filters.brands}
            stores={filters.stores}
            categories={filters.categories}
            cities={filters.cities}
            abcClassifications={filters.abcClassifications}
            onBrandsChange={(brands) => setFilters({ ...filters, brands })}
            onStoresChange={(stores) => setFilters({ ...filters, stores })}
            onCategoriesChange={(categories) => setFilters({ ...filters, categories })}
            onCitiesChange={(cities) => setFilters({ ...filters, cities })}
            onAbcClassificationsChange={(abcClassifications) => setFilters({ ...filters, abcClassifications })}
            onClearAll={() => setFilters({ brands: [], stores: [], categories: [], cities: [], abcClassifications: [], root_cause: undefined, is_resolved: undefined, start_date: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], end_date: new Date().toISOString().split('T')[0] })}
            showCountries={false}
            showRetailers={false}
            showBrands={true}
            showStores={true}
            showCategories={true}
            showCities={true}
            showAbcClassifications={true}
            customFilters={
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    size="small"
                    type="date"
                    label="Start Date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    size="small"
                    type="date"
                    label="End Date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    size="small"
                    options={['brand_supply_issue', 'forecast_accuracy_issue', 'distributor_issue', 'planning_issue', 'unknown']}
                    value={filters.root_cause || null}
                    onChange={(_, newValue) => setFilters({ ...filters, root_cause: newValue || undefined })}
                    renderInput={(params) => <TextField {...params} label="Root Cause" />}
                    getOptionLabel={(option) => formatRootCause(option)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    size="small"
                    options={[
                      { label: 'All', value: undefined },
                      { label: 'Unresolved Only', value: false },
                      { label: 'Resolved Only', value: true },
                    ]}
                    value={filters.is_resolved === undefined ? { label: 'All', value: undefined } : filters.is_resolved ? { label: 'Resolved Only', value: true } : { label: 'Unresolved Only', value: false }}
                    onChange={(_, newValue) => setFilters({ ...filters, is_resolved: newValue?.value })}
                    renderInput={(params) => <TextField {...params} label="Status" />}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                  />
                </Grid>
              </>
            }
          />
        </Box>

        {/* Summary Cards */}
        {loadingSummary ? (
          <CircularProgress />
        ) : summary ? (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                  Current Stock-Outs
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                  {summary.current_stockouts}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                  Critical Stock-Outs
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-danger)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                  {summary.critical_stockouts}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                  Lost Revenue
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                  {fmtCurrency(summary.total_lost_revenue)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                  Trend Direction
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1 }}>
                  {getTrendIcon(summary.trend_direction)}
                  <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                    {summary.trend_direction}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        ) : null}

        {/* Monthly Stock-Out Value Chart */}
        {monthlyChartData.length > 0 && (
          <Box ref={chartContainerRef} sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: 3, mb: 3 }}>
            <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', mb: 2 }}>
              Monthly Stock-Out Value
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#8A8A85' }} tickLine={false} axisLine={{ stroke: '#E5E5E0' }} />
                <YAxis tick={{ fontSize: 11, fill: '#8A8A85' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => fmtCurrency(v)} />
                <Tooltip
                  formatter={(value: any, name: string) => [fmtCurrency(value), formatRootCause(name)]}
                  contentStyle={chartTooltipStyle}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} formatter={(val: string) => formatRootCause(val)} />
                {rootCauseKeys.map((rc, idx) => (
                  <Bar
                    key={rc}
                    dataKey={rc}
                    stackId="stockout"
                    name={rc}
                    fill={getRootCauseColor(rc) || COLORS[idx % COLORS.length]}
                    radius={idx === rootCauseKeys.length - 1 ? [3, 3, 0, 0] : undefined}
                  />
                ))}
                {rootCauseKeys.length === 0 && (
                  <Bar dataKey="lost_revenue" name={`Lost Revenue ${fmtCurrencyLabel()}`} fill="#722F37" radius={[3, 3, 0, 0]} />
                )}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* Export + Tab navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
          <ExportMenu
            data={(enhancedStockouts?.stockouts || []).map((s: any) => ({
              Product: s.product.name, SKU: s.product.sku, Brand: s.product.brand,
              Store: s.store.name, City: s.store.city,
              'Duration (days)': s.stockout_duration_days,
              'Start Date': s.stockout_start_date?.slice(0, 10),
              'Root Cause': s.root_cause, Confidence: `${(s.root_cause_confidence || 0).toFixed(0)}%`,
              'Lost Revenue': s.lost_revenue_value, 'Lost Units': s.lost_units_estimated,
              Status: s.is_resolved ? 'Resolved' : 'Active',
            }))}
            filename={`StockOut_${new Date().toISOString().slice(0, 10)}`}
            title="Stock-Out Management"
            chartRef={chartContainerRef}
          />
          <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />
        </Box>
        <Box sx={{ borderBottom: '1px solid var(--color-border)', mb: 3 }}>
          <Tabs value={currentTab} onChange={(_, val) => setCurrentTab(val)} variant="scrollable" scrollButtons="auto">
            <Tab label={`Active Stock-Outs (${enhancedStockouts?.total_count || 0})`} />
            <Tab label="Root Cause Analysis" />
            <Tab label="Trends & Patterns" />
            <Tab label="Prevention Recommendations" />
          </Tabs>
        </Box>

        {/* Active Stock-Outs Tab - ENHANCED */}
        {currentTab === 0 && (
          <Box>
            <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', mb: 2 }}>
              Stock-Out Periods with Root Cause Analysis
            </Typography>
            {loadingEnhancedStockouts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Store</TableCell>
                      <TableCell>Item Status</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Lost Sales (Forecast-based)</TableCell>
                      <TableCell>Root Cause</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell>Recommended Action</TableCell>
                      <TableCell>Resolution Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enhancedStockouts && enhancedStockouts.stockouts.length > 0 ? (
                      enhancedStockouts.stockouts.map((stockout) => (
                        <TableRow key={stockout.stockout_id} hover>
                          <TableCell>
                            <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-primary)' }}>{stockout.product.name}</Typography>
                            <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                              {stockout.product.sku} &bull; {stockout.product.brand}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{stockout.store.name}</Typography>
                            <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                              {stockout.store.city}, {stockout.store.country}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: '6px', px: '8px' }}>
                            {(stockout as any).lifecycle_stage ? (
                              <Chip
                                label={(stockout as any).lifecycle_stage}
                                size="small"
                                color={
                                  (stockout as any).lifecycle_stage === 'NEW' ? 'info' :
                                  (stockout as any).lifecycle_stage === 'GROWTH' ? 'success' :
                                  (stockout as any).lifecycle_stage === 'DECLINING' ? 'warning' :
                                  ((stockout as any).lifecycle_stage === 'PHASE_OUT' || (stockout as any).lifecycle_stage === 'DISCONTINUED') ? 'error' :
                                  'default'
                                }
                                variant="outlined"
                                sx={{ fontSize: '10px', height: 20 }}
                              />
                            ) : (
                              <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <TimeIcon fontSize="small" sx={{ mr: 0.5, color: 'var(--color-text-muted)', fontSize: 14 }} />
                              <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{stockout.stockout_duration_days} days</Typography>
                            </Box>
                            <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                              {new Date(stockout.stockout_start_date).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-danger)' }}>
                              {fmtCurrency(stockout.lost_revenue_value)}
                            </Typography>
                            <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                              {stockout.lost_units_estimated.toFixed(0)} units
                            </Typography>
                            <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                              ({stockout.forecasted_daily_sales.toFixed(1)}/day)
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <MuiTooltip title={stockout.root_cause_details ? JSON.stringify(stockout.root_cause_details) : ''}>
                              <span
                                className="status-badge"
                                style={{
                                  backgroundColor: getRootCauseColor(stockout.root_cause),
                                  color: 'white',
                                }}
                              >
                                {formatRootCause(stockout.root_cause)}
                              </span>
                            </MuiTooltip>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={stockout.root_cause_confidence || 0}
                                color={getConfidenceColor(stockout.root_cause_confidence || 0)}
                                sx={{ width: 60, height: 8, borderRadius: 1 }}
                              />
                              <Typography sx={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
                                {(stockout.root_cause_confidence || 0).toFixed(0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const announcedMatch = announcedStockouts.find(
                                (a) => a.sku === stockout.product.sku
                              );
                              const recommendation = getRecommendationForRootCause(
                                stockout.root_cause,
                                announcedMatch,
                              );
                              return (
                                <Box>
                                  <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
                                    {recommendation}
                                  </Typography>
                                  {announcedMatch && (
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => navigate('/replenishment/settings')}
                                      sx={{ fontSize: '10px', textTransform: 'none', p: 0, mt: 0.5, minWidth: 0, color: 'var(--color-info)' }}
                                    >
                                      View announced stock-outs
                                    </Button>
                                  )}
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => handleOpenActionPlan(stockout)}
                                    sx={{ fontSize: '10px', textTransform: 'none', p: 0, mt: 0.5, ml: announcedMatch ? 1 : 0, minWidth: 0, color: 'var(--color-text-muted)' }}
                                  >
                                    Full action plan
                                  </Button>
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <span className={stockout.is_resolved ? 'status-badge status-badge--success' : 'status-badge status-badge--warning'}>
                              {stockout.is_resolved ? 'Resolved' : 'Active'}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                            No stock-outs found matching the current filters
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Root Cause Analysis Tab - ENHANCED */}
        {currentTab === 1 && (
          <Grid container spacing={3}>
            {/* Summary Cards */}
            {rootCauseAnalytics && (() => {
              // Calculate totals from analytics data
              const totalStockouts = rootCauseAnalytics.analytics_by_root_cause.reduce((sum, item) => sum + item.stockout_count, 0);
              const totalRevenue = rootCauseAnalytics.analytics_by_root_cause.reduce((sum, item) => sum + item.total_lost_revenue, 0);

              return (
                <>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                          <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                            Total Stockouts Analyzed
                          </Typography>
                          <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                            {totalStockouts}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                          <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                            Total Lost Revenue
                          </Typography>
                          <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-danger)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                            {fmtCurrency(totalRevenue)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                          <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                            Root Causes Identified
                          </Typography>
                          <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                            {rootCauseAnalytics.analytics_by_root_cause.length}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>

                {/* Root Cause Distribution Charts */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: 3 }}>
                    <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', mb: 2 }}>
                      Root Cause Distribution (by Count)
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={rootCauseChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {rootCauseChartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={getRootCauseColor(entry.name.toLowerCase().replace(/ /g, '_')) || COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={chartTooltipStyle} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: 3 }}>
                    <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', mb: 2 }}>
                      Lost Revenue by Root Cause
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={rootCauseChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} tick={{ fontSize: 10, fill: '#8A8A85' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#8A8A85' }} tickLine={false} axisLine={false} />
                        <Tooltip formatter={(value: any) => fmtCurrency(value)} contentStyle={chartTooltipStyle} />
                        <Bar dataKey="revenue" fill="#722F37" name={`Lost Revenue ${fmtCurrencyLabel()}`} radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>

                {/* Detailed Analytics Table */}
                <Grid item xs={12}>
                  <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: 3 }}>
                    <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', mb: 2 }}>
                      Detailed Root Cause Analytics
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Root Cause</TableCell>
                            <TableCell align="right">Count</TableCell>
                            <TableCell align="right">Percentage</TableCell>
                            <TableCell align="right">Lost Revenue</TableCell>
                            <TableCell align="right">Avg Duration</TableCell>
                            <TableCell align="right">Lost Units</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rootCauseAnalytics.analytics_by_root_cause
                            .sort((a, b) => b.stockout_count - a.stockout_count)
                            .map((item) => (
                              <TableRow key={item.root_cause}>
                                <TableCell>
                                  <span
                                    className="status-badge"
                                    style={{
                                      backgroundColor: getRootCauseColor(item.root_cause),
                                      color: 'white',
                                    }}
                                  >
                                    {formatRootCause(item.root_cause)}
                                  </span>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-primary)' }}>{item.stockout_count}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={(item.stockout_count / totalStockouts) * 100}
                                      sx={{ width: 60, height: 8, borderRadius: 1 }}
                                    />
                                    <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                                      {((item.stockout_count / totalStockouts) * 100).toFixed(1)}%
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-danger)' }}>
                                    {fmtCurrency(item.total_lost_revenue)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                                    {item.average_duration_days.toFixed(1)} days
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                                    {item.total_lost_units.toFixed(0)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Grid>
              </>
              );
            })()}

            {/* Fallback to legacy data if analytics not available */}
            {!rootCauseAnalytics && rootCause && (
              <>
                <Grid item xs={12} md={6}>
                  <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: 3 }}>
                    <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', mb: 2 }}>
                      Root Cause Distribution
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={rootCauseChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {rootCauseChartData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: 3 }}>
                    <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', mb: 2 }}>
                      Severity Breakdown
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={Object.entries(rootCause.severity_breakdown).map(([sev, count]) => ({
                        severity: sev,
                        count
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="severity" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#B76E79" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: 3 }}>
                    <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', mb: 2 }}>
                      Root Cause Details
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Root Cause</TableCell>
                            <TableCell align="right">Count</TableCell>
                            <TableCell align="right">Total Duration</TableCell>
                            <TableCell align="right">Lost Revenue</TableCell>
                            <TableCell align="right">Affected Products</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(rootCause.cause_breakdown).map(([cause, data]) => (
                            <TableRow key={cause}>
                              <TableCell>{cause.replace(/_/g, ' ').toUpperCase()}</TableCell>
                              <TableCell align="right">{data.count}</TableCell>
                              <TableCell align="right">{data.avg_duration_days} days</TableCell>
                              <TableCell align="right">{fmtCurrency(data.total_lost_revenue)}</TableCell>
                              <TableCell align="right">{data.affected_products}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        )}

        {/* Trends Tab */}
        {currentTab === 2 && trends && (
          <Box>
            <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', mb: 2 }}>
              Stock-Out Trends ({trends.period_days} days)
            </Typography>

            <Alert
              severity={trends.trend_direction === 'increasing' ? 'error' : trends.trend_direction === 'decreasing' ? 'success' : 'info'}
              sx={{ mb: 3 }}
            >
              <Typography sx={{ fontSize: 'var(--text-sm)' }}>
                Stock-outs are <strong>{trends.trend_direction}</strong> over the past {trends.period_days} days.
                Total stock-outs in period: {trends.total_stockouts}
              </Typography>
            </Alert>

            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={trends.weekly_trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                <XAxis dataKey="week_start" tick={{ fontSize: 10, fill: '#8A8A85' }} tickLine={false} axisLine={{ stroke: '#E5E5E0' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#8A8A85' }} tickLine={false} axisLine={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#8A8A85' }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="stockout_count"
                  stroke="#722F37"
                  strokeWidth={2}
                  dot={{ r: 3, fill: '#722F37' }}
                  name="Stock-Outs"
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="critical_count"
                  stroke="#B76E79"
                  strokeWidth={2}
                  dot={{ r: 3, fill: '#B76E79' }}
                  name="Critical"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="total_lost_revenue"
                  stroke="#C9A96E"
                  strokeWidth={2}
                  dot={{ r: 3, fill: '#C9A96E' }}
                  name={`Lost Revenue ${fmtCurrencyLabel()}`}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* Prevention Recommendations Tab */}
        {currentTab === 3 && (
          <Box>
            <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', mb: 2 }}>
              Prevention Recommendations
            </Typography>

            {recommendations && recommendations.length > 0 ? (
              <List>
                {recommendations.map((rec, index) => (
                  <ListItem key={index} sx={{ mb: 2, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', bgcolor: 'var(--color-surface)' }}>
                    <ListItemIcon>
                      <RecommendationIcon sx={{ fontSize: 40, color: 'var(--color-accent)' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>{rec.title}</Typography>
                          <span className={getPriorityBadgeClass(rec.priority)}>
                            {rec.priority}
                          </span>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', mb: 0.5 }}>
                            <strong>Product:</strong> {rec.product_name}
                          </Typography>
                          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', mb: 0.5 }}>
                            <strong>Issue:</strong> {rec.description}
                          </Typography>
                          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-info)', mb: 0.5 }}>
                            <strong>Suggested Action:</strong> {rec.suggested_action}
                          </Typography>
                          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-success)' }}>
                            <strong>Expected Impact:</strong> {rec.expected_impact}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="success">
                <Typography sx={{ fontSize: 'var(--text-sm)' }}>
                  No recommendations at this time. Your inventory management is performing well!
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </Box>

      {/* Action Plan Dialog */}
      <Dialog
        open={actionPlanDialogOpen}
        onClose={handleCloseDialogs}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Recommended Action Plan</DialogTitle>
        <DialogContent>
          {selectedStockout && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Product</Typography>
                  <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                    {selectedStockout.product.name}
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    {selectedStockout.product.sku} &bull; {selectedStockout.product.brand}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Store</Typography>
                  <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                    {selectedStockout.store.name}
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    {selectedStockout.store.city}, {selectedStockout.store.country}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-primary)', mb: 1 }}>
                  Root Cause Analysis
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <span
                    className="status-badge"
                    style={{
                      backgroundColor: getRootCauseColor(selectedStockout.root_cause),
                      color: 'white',
                    }}
                  >
                    {formatRootCause(selectedStockout.root_cause)}
                  </span>
                  <span className={`status-badge status-badge--${getConfidenceColor(selectedStockout.root_cause_confidence || 0) === 'success' ? 'success' : getConfidenceColor(selectedStockout.root_cause_confidence || 0) === 'warning' ? 'warning' : 'neutral'}`}>
                    {(selectedStockout.root_cause_confidence || 0).toFixed(0)}% Confidence
                  </span>
                </Box>
                {selectedStockout.root_cause_details && (
                  <Box sx={{ p: 2, mt: 2, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', bgcolor: 'var(--color-bg-secondary)' }}>
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', mb: 0.5 }}>
                      Details:
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                      {typeof selectedStockout.root_cause_details === 'string'
                        ? selectedStockout.root_cause_details
                        : JSON.stringify(selectedStockout.root_cause_details, null, 2)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-primary)', mb: 1 }}>
                  Recommended Action
                </Typography>
                {(() => {
                  const announcedMatch = announcedStockouts.find(
                    (a) => a.sku === selectedStockout.product.sku
                  );
                  const recommendation = getRecommendationForRootCause(
                    selectedStockout.root_cause,
                    announcedMatch,
                  );
                  return (
                    <>
                      <Box sx={{ p: 2, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', bgcolor: 'var(--color-info-bg)' }}>
                        <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', whiteSpace: 'pre-line' }}>
                          {recommendation}
                        </Typography>
                      </Box>
                      {announcedMatch && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography sx={{ fontSize: 'var(--text-sm)' }}>
                            <strong>Supplier-announced stock-out</strong> for {announcedMatch.product_name} ({announcedMatch.sku}):
                            from {new Date(announcedMatch.start_date).toLocaleDateString()}
                            {announcedMatch.end_date ? ` to ${new Date(announcedMatch.end_date).toLocaleDateString()}` : ' (no end date announced)'}
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => { handleCloseDialogs(); navigate('/replenishment/settings'); }}
                            sx={{ fontSize: 'var(--text-xs)', textTransform: 'none', p: 0, mt: 0.5 }}
                          >
                            Go to Replenishment Settings &rarr;
                          </Button>
                        </Alert>
                      )}
                    </>
                  );
                })()}
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-primary)', mb: 1 }}>
                  Impact Assessment
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Duration</Typography>
                      <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-danger)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                        {selectedStockout.stockout_duration_days} days
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Lost Revenue</Typography>
                      <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-danger)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                        {fmtCurrency(selectedStockout.lost_revenue_value)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Lost Units</Typography>
                      <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-danger)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                        {selectedStockout.lost_units_estimated.toFixed(0)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setActionPlanDialogOpen(false);
              handleOpenOrderHistory(selectedStockout);
            }}
            sx={{ mr: 'auto', textTransform: 'none', fontSize: 'var(--text-sm)' }}
          >
            View Order History
          </Button>
          <Button onClick={handleCloseDialogs}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Order History Dialog */}
      <Dialog
        open={orderHistoryDialogOpen}
        onClose={handleCloseDialogs}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Detailed Order History</DialogTitle>
        <DialogContent>
          {loadingOrderHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : orderHistory ? (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Product</Typography>
                  <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                    {orderHistory.product.product_name}
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    SKU: {orderHistory.product.sku} &bull; Brand: {orderHistory.product.brand}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Store</Typography>
                  <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                    {orderHistory.store.store_name}
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    {orderHistory.store.retailer} &bull; {orderHistory.store.country}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Current Status */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InventoryIcon sx={{ color: 'var(--color-text-muted)', fontSize: 18 }} />
                    <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>Current Inventory Status</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Current Stock</Typography>
                        <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>{orderHistory.current_status.current_stock}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>In Transit</Typography>
                        <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>{orderHistory.current_status.in_transit}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Avg Daily Sales</Typography>
                        <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>{Math.round(orderHistory.current_status.avg_daily_sales ?? 0)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Days of Supply</Typography>
                        <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', fontVariantNumeric: 'tabular-nums', mt: 0.5, color: orderHistory.current_status.days_of_supply < 7 ? 'var(--color-danger)' : 'var(--color-success)' }}>
                          {orderHistory.current_status.days_of_supply}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Order Summary */}
              <Accordion defaultExpanded sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShippingIcon sx={{ color: 'var(--color-text-muted)', fontSize: 18 }} />
                    <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>Order Summary (90 days)</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                        <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Total Orders</Typography>
                        <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>{orderHistory.summary.total_orders}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                        <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Fulfillment Rate</Typography>
                        <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', fontVariantNumeric: 'tabular-nums', mt: 0.5, color: orderHistory.summary.fulfillment_rate_pct < 80 ? 'var(--color-danger)' : 'var(--color-success)' }}>
                          {orderHistory.summary.fulfillment_rate_pct}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                        <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Avg Delay</Typography>
                        <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', fontVariantNumeric: 'tabular-nums', mt: 0.5, color: orderHistory.summary.avg_delay_days > 3 ? 'var(--color-danger)' : 'var(--color-info)' }}>
                          {orderHistory.summary.avg_delay_days} days
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                        <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Delayed Orders</Typography>
                        <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>{orderHistory.summary.delayed_orders_count}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                        <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Pending/Stuck</Typography>
                        <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', fontVariantNumeric: 'tabular-nums', mt: 0.5, color: orderHistory.summary.pending_stuck_orders > 0 ? 'var(--color-warning)' : 'var(--color-text-primary)' }}>
                          {orderHistory.summary.pending_stuck_orders}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                        <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Cancelled</Typography>
                        <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>{orderHistory.summary.cancelled_orders}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Issues Detected */}
              {Object.values(orderHistory.issues_detected).some((v: any) => v === true) && (
                <Accordion sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon sx={{ color: 'var(--color-danger)', fontSize: 18 }} />
                      <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>Issues Detected</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {orderHistory.issues_detected.has_pending_stuck_orders && (
                        <ListItem>
                          <ListItemIcon><ErrorIcon sx={{ color: 'var(--color-danger)' }} /></ListItemIcon>
                          <ListItemText
                            primary="Stuck/Pending Orders"
                            secondary="Some orders are delayed beyond expected delivery time"
                          />
                        </ListItem>
                      )}
                      {orderHistory.issues_detected.has_high_cancellation_rate && (
                        <ListItem>
                          <ListItemIcon><ErrorIcon sx={{ color: 'var(--color-danger)' }} /></ListItemIcon>
                          <ListItemText
                            primary="High Cancellation Rate"
                            secondary="More than 20% of orders are being cancelled"
                          />
                        </ListItem>
                      )}
                      {orderHistory.issues_detected.has_poor_fulfillment && (
                        <ListItem>
                          <ListItemIcon><ErrorIcon sx={{ color: 'var(--color-danger)' }} /></ListItemIcon>
                          <ListItemText
                            primary="Poor Fulfillment Rate"
                            secondary="Fulfillment rate is below 80%"
                          />
                        </ListItem>
                      )}
                      {orderHistory.issues_detected.has_frequent_delays && (
                        <ListItem>
                          <ListItemIcon><WarningIcon sx={{ color: 'var(--color-warning)' }} /></ListItemIcon>
                          <ListItemText
                            primary="Frequent Delays"
                            secondary="More than 30% of orders are delayed"
                          />
                        </ListItem>
                      )}
                      {orderHistory.issues_detected.insufficient_order_frequency && (
                        <ListItem>
                          <ListItemIcon><WarningIcon sx={{ color: 'var(--color-warning)' }} /></ListItemIcon>
                          <ListItemText
                            primary="Insufficient Order Frequency"
                            secondary="Orders are placed too infrequently for the lead time"
                          />
                        </ListItem>
                      )}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Order Details Table */}
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimelineIcon sx={{ color: 'var(--color-text-muted)', fontSize: 18 }} />
                    <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                      Order Details ({orderHistory.orders.length} orders)
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Order Date</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell>Order Status</TableCell>
                          <TableCell>Expected Delivery</TableCell>
                          <TableCell>Actual Delivery</TableCell>
                          <TableCell>Delay</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orderHistory.orders.map((order: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                                {order.order_date ? new Date(order.order_date).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                                {order.quantity_ordered} ordered
                              </Typography>
                              {order.quantity_delivered > 0 && (
                                <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                                  {order.quantity_delivered} delivered
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className={
                                order.status === 'delivered' ? 'status-badge status-badge--success' :
                                order.status === 'cancelled' ? 'status-badge status-badge--danger' :
                                order.is_stuck ? 'status-badge status-badge--warning' : 'status-badge status-badge--neutral'
                              }>
                                {order.status}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                                {order.expected_delivery ? new Date(order.expected_delivery).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                                {order.actual_delivery ? new Date(order.actual_delivery).toLocaleDateString() : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {order.delay_days !== null && order.delay_days !== undefined ? (
                                <span className={order.delay_days > 0 ? 'status-badge status-badge--danger' : 'status-badge status-badge--success'}>
                                  {order.delay_days} days
                                </span>
                              ) : order.is_stuck ? (
                                <span className="status-badge status-badge--warning">
                                  Stuck {order.days_stuck} days
                                </span>
                              ) : (
                                <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>-</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Box>
          ) : (
            <Alert severity="info">
              No order history data available for this stockout.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </FullscreenWrap>
  );
};

export default StockOutManagement;
